import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MenuItem, TextField } from "@mui/material";

import type { EventLogEntry } from "@core/apis/shared";

import SolidCard from "../../ui/analytics/cards/SolidCard/SolidCard";
import { ALL_EVENT_TYPES, formatEventType } from "./event-log.utils";
import { EventsTable } from "./EventsTable";

type EventsSectionProps = Readonly<{
  /** Event log entries to render. Each app fetches these via the shared hook. */
  events?: EventLogEntry[];
  isLoading?: boolean;
}>;

/**
 * Presentational event-log section: owns only the type filter, card chrome and
 * table. Data is injected by the consuming app (propai-web / platform-web), so
 * this component has no data-fetching dependency.
 */
export function EventsSection({ events = [], isLoading = false }: EventsSectionProps) {
  const { t } = useTranslation(["propai_properties", "_common"]);
  const [selectedType, setSelectedType] = useState<string>(ALL_EVENT_TYPES);

  const availableTypes = useMemo(
    () => Array.from(new Set(events.map((event) => event.type))).sort((a, b) => a.localeCompare(b)),
    [events]
  );

  const filteredEvents = useMemo(
    () =>
      selectedType === ALL_EVENT_TYPES
        ? events
        : events.filter((event) => event.type === selectedType),
    [events, selectedType]
  );

  return (
    <SolidCard
      title={t("events.title")}
      titleVariant="titleMedium"
      testId="events-table"
      titleAddon={
        <TextField
          select
          size="small"
          label={t("events.filter_label")}
          value={selectedType}
          onChange={(event) => setSelectedType(event.target.value)}
          sx={{ minWidth: 220 }}
          disabled={isLoading}
        >
          <MenuItem value={ALL_EVENT_TYPES}>{t("events.filter_all_types")}</MenuItem>
          {availableTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {formatEventType(t, type)}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      <EventsTable events={filteredEvents} isLoading={isLoading} />
    </SolidCard>
  );
}

export default EventsSection;
